import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../state/store";
import { addToCart } from "../state/slices/cartSlice";
import { useAuth } from "../context/AuthContext";
import "./ProductCard.css";
import CustomAlert from "./CustomAlert";
import { Product } from "../interfaces/home_entity";
import { calculatePrice } from "../utils/pricing";

interface ProductCardProps {
  product: Product;
  requireDiscount?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  requireDiscount = false,
}) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();

  const cartStatus = useSelector((state: RootState) => state.cart.status);
  const cartError = useSelector((state: RootState) => state.cart.error);

  const isLoggedIn = !!currentUser;
  const userId = currentUser?.id || "guest";
   // ✅ Use shared pricing logic
  const { finalPrice, originalFinalPrice, discountPercent } =
    calculatePrice(product);

  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => setShowAlert(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showAlert]);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isLoggedIn) {
      setAlertMessage("Please login to add items to cart");
      setAlertType("error");
      setShowAlert(true);
      return;
    }

    try {
      await dispatch(
        addToCart({
          productId: product.id,
          userId,
          price: finalPrice
        })
      ).unwrap();

      setAlertMessage(`${product.name} added to cart!`);
      setAlertType("success");
    } catch (error) {
      console.error("Error adding to cart:", error);
      setAlertMessage(cartError || "Failed to add to cart.");
      setAlertType("error");
    }

    setShowAlert(true);
  };

  const handleClick = () => {
    navigate(`/product/${product.id}`);
  };

 

  // ✅ Conditional render AFTER calculation
  if (requireDiscount && discountPercent <= 0) {
    return null;
  }

  const imageSrc = (product.images?.[0] || "/placeholder.png");

  return (
    <div className="product-card-container">
      {showAlert && (
        <CustomAlert
          message={alertMessage}
          type={alertType}
          onClose={() => setShowAlert(false)}
        />
      )}

      <div className="product-card" onClick={handleClick}>
        <img src={imageSrc} alt={product.name} />

        <div className="product-info">
          <h3>{product.name}</h3>

          <div className="price">
            {discountPercent > 0 ? (
              <>
                <span className="discounted-price">
                  Rs.{finalPrice.toFixed(2)}
                </span>
                <span className="original-price">
                  Rs.{originalFinalPrice.toFixed(2)}
                </span>
                <span className="discount-percent">
                  ({discountPercent}% OFF)
                </span>
              </>
            ) : (
              <span className="discounted-price">
                Rs.{finalPrice.toFixed(2)}
              </span>
            )}
          </div>
        </div>

        <button
          className="add-to-cart-btn"
          onClick={handleAddToCart}
          disabled={cartStatus === "loading"}
        >
          {cartStatus === "loading" ? "Adding..." : "Add to Cart"}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;